import {
  FileTextOutlined,
  InboxOutlined,
  PaperClipOutlined
} from "@ant-design/icons";
import {
  Button,
  Divider,
  Drawer,
  Input,
  message,
  Spin,
  Upload,
  type UploadFile,
  type UploadProps
} from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { useEffect, useMemo, useState } from "react";
import "./bootstrap-design-scope.scss";
import {
  catalogImportHasRowFailures,
  catalogImportSummaryStats,
  maybeDownloadImportFailureReport,
  type DesignScopeImportLogStats
} from "./import-log-result";
import ImportLogSummaryPanel from "./import-log-summary-panel";
import { useBootstrapDesignScopeApis } from "./use-bootstrap-design-scope-apis";
import {
  DESIGN_SCOPE_CSV_FILE_ERROR,
  DESIGN_SCOPE_LIST_IMPORTED
} from "./design-scope-toast-messages";

export type ImportDesignScopeDrawerProps = {
  open: boolean;
  onClose: () => void;
  onImported?: () => void;
  existingListNames?: string[];
};

function normalizeListName(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function ImportDesignScopeDrawer({
  open,
  onClose,
  onImported,
  existingListNames = []
}: ImportDesignScopeDrawerProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [importSummary, setImportSummary] =
    useState<DesignScopeImportLogStats | null>(null);

  const {
    downloadImportTemplate,
    downloadingTemplate,
    importCatalog,
    importingCatalog
  } = useBootstrapDesignScopeApis();

  const isImportInProgress = importingCatalog;
  const showSummary = Boolean(importSummary);

  const resetFormState = () => {
    setFileList([]);
    setTitle("");
    setDescription("");
    setImportSummary(null);
  };

  useEffect(() => {
    if (open) return;
    resetFormState();
  }, [open]);

  const selectedFile = fileList[0];
  const normalizedTitle = normalizeListName(title);
  const duplicateListName =
    normalizedTitle.length > 0 &&
    existingListNames.some(
      (name) => normalizeListName(name) === normalizedTitle
    );
  const canImport =
    Boolean(selectedFile && title.trim()) &&
    !duplicateListName &&
    !isImportInProgress &&
    !showSummary;

  const uploadProps: UploadProps = useMemo(
    () => ({
      accept: ".csv,text/csv",
      multiple: false,
      maxCount: 1,
      showUploadList: false,
      beforeUpload: (file) => {
        const isCsv =
          file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
        if (!isCsv) {
          message.error(DESIGN_SCOPE_CSV_FILE_ERROR);
          return Upload.LIST_IGNORE;
        }
        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: "done",
            originFileObj: file
          }
        ]);
        return false;
      },
      onRemove: () => {
        setFileList([]);
      }
    }),
    []
  );

  const handleDownloadTemplate = () => {
    downloadImportTemplate();
  };

  const handleClose = () => {
    if (isImportInProgress) return;
    onClose();
  };

  const handleBackToUpload = () => {
    setImportSummary(null);
  };

  const handleImport = async () => {
    if (!canImport || !selectedFile?.originFileObj) return;

    const catalogName = title.trim();
    setImportSummary(null);

    const result = await importCatalog({
      file: selectedFile.originFileObj as File,
      catalogName,
      description: description.trim() || undefined
    });

    if (!result.success || !result.data) return;

    onImported?.();

    if (catalogImportHasRowFailures(result.data)) {
      const stats = catalogImportSummaryStats(result.data);
      setImportSummary(stats);
      maybeDownloadImportFailureReport(stats);
      return;
    }

    message.success(DESIGN_SCOPE_LIST_IMPORTED);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Import Design Scope List"
      size={480}
      destroyOnHidden
      closable={!isImportInProgress}
      mask={{ closable: !isImportInProgress }}
      keyboard={!isImportInProgress}
      className="bootstrap-ds-import-drawer"
      data-testid="bootstrap-ds-import-drawer"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleClose}
            disabled={isImportInProgress}
            data-testid="bootstrap-ds-import-cancel"
          >
            {showSummary ? "Close" : "Cancel"}
          </Button>
          {!isImportInProgress && !showSummary && (
            <Button
              type="primary"
              className="bg-ciq-primary-800 border-ciq-primary-800"
              disabled={!canImport}
              loading={importingCatalog}
              onClick={() => void handleImport()}
              data-testid="bootstrap-ds-import-submit"
            >
              Import
            </Button>
          )}
        </div>
      }
    >
      {showSummary && importSummary ? (
        <ImportLogSummaryPanel
          stats={importSummary}
          description="Design Scope catalog import complete. Review the summary below."
          onBack={handleBackToUpload}
          backLabel="Back to upload"
        />
      ) : (
        <div className="flex flex-col gap-5">
          <section data-testid="bootstrap-ds-download-format">
            <div className="font-semibold text-ciq-gray-950">
              Download Format
            </div>
            <div className="mt-1 text-xs text-black text-opacity-50">
              Download pre-formatted .csv file to import a Design scope list
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 rounded border border-solid border-ciq-gray-200 px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <FileTextOutlined className="text-base text-ciq-primary-800" />
                <span className="truncate text-sm text-ciq-gray-950">
                  Standard Format .csv
                </span>
              </div>
              <Button
                loading={downloadingTemplate}
                disabled={downloadingTemplate || isImportInProgress}
                onClick={handleDownloadTemplate}
                data-testid="bootstrap-ds-download-template"
              >
                Download
              </Button>
            </div>
          </section>

          <Divider className="my-0" />

          <section data-testid="bootstrap-ds-upload-section">
            <div className="font-semibold text-ciq-gray-950">Upload file</div>
            <div className="mt-3">
              <Dragger
                {...uploadProps}
                disabled={isImportInProgress}
                data-testid="bootstrap-ds-upload-dragger"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to upload</p>
                <p className="ant-upload-hint">Only CSV files are supported</p>
              </Dragger>
            </div>
          </section>

          {selectedFile && (
            <section
              className="rounded border border-solid border-ciq-gray-200 p-3"
              data-testid="bootstrap-ds-file-details"
            >
              <div className="mb-3 flex items-center gap-2 text-sm font-medium text-ciq-gray-950">
                <PaperClipOutlined />
                <span className="truncate" title={selectedFile.name}>
                  {selectedFile.name}
                </span>
              </div>

              <div className="mb-3">
                <label
                  className="mb-1 block text-sm font-semibold text-ciq-gray-950"
                  htmlFor="bootstrap-ds-import-title"
                >
                  Title<span className="text-red-500">*</span>
                </label>
                <Input
                  id="bootstrap-ds-import-title"
                  placeholder="Type"
                  value={title}
                  status={duplicateListName ? "error" : undefined}
                  disabled={isImportInProgress}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="bootstrap-ds-import-title"
                />
                {duplicateListName && (
                  <div
                    className="mt-1 text-xs text-ciq-red-700"
                    data-testid="bootstrap-ds-import-title-error"
                  >
                    A list with this name already exists. Open the list to
                    import into it.
                  </div>
                )}
              </div>

              <div>
                <label
                  className="mb-1 block text-sm font-semibold text-ciq-gray-950"
                  htmlFor="bootstrap-ds-import-description"
                >
                  Description
                </label>
                <Input.TextArea
                  id="bootstrap-ds-import-description"
                  placeholder="Type"
                  value={description}
                  disabled={isImportInProgress}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  data-testid="bootstrap-ds-import-description"
                />
              </div>
            </section>
          )}

          {isImportInProgress && (
            <div
              className="flex flex-col items-center gap-2 py-4"
              data-testid="bootstrap-ds-import-processing"
            >
              <Spin size="medium" />
              <p className="m-0 text-sm font-semibold">Import in progress</p>
              <p className="m-0 text-center text-xs text-black text-opacity-50">
                Import may take a couple of minutes. Please remain on this page
                until import is complete
              </p>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default ImportDesignScopeDrawer;
